import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Sheet, Textarea, Typography } from '@mui/joy';
import { Sparkle } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/apiError';

interface Props {
  profileId: number;
  target: 'business' | 'service';
  value: string;
  onChange: (value: string) => void;
  businessName?: string;
  primaryCategory?: string;
  serviceName?: string;
}

interface DraftStatus {
  status: 'pending' | 'completed' | 'failed';
  description?: string;
  error?: string;
}

/** AI produces a draft; only Use description changes the editable form. */
export function GBPDescriptionEditor({ profileId, target, value, onChange, businessName, primaryCategory, serviceName }: Props) {
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const maxLength = target === 'business' ? 750 : 300;
  const contextKey = JSON.stringify([profileId, target, businessName, primaryCategory, serviceName]);

  useEffect(() => {
    generation.current += 1;
    setGenerating(false);
    setDraft(null);
    setError(null);
    return () => { generation.current += 1; };
  }, [contextKey]);

  const generate = async () => {
    const request = ++generation.current;
    setGenerating(true);
    setDraft(null);
    setError(null);
    const url = `/api/marketing/gbp-profiles/${profileId}`;
    try {
      const { data } = await api.post<{ task_id: string }>(`${url}/generate-description/`, {
        target, current_description: value,
        ...(businessName !== undefined ? { business_name: businessName } : {}),
        ...(primaryCategory !== undefined ? { primary_category: primaryCategory } : {}),
        ...(target === 'service' ? { service_name: serviceName?.trim() } : {}),
      });
      if (!data.task_id) throw new Error('The description could not be started. Please try again.');
      for (let attempt = 0; attempt < 90 && generation.current === request; attempt++) {
        const { data: result } = await api.get<DraftStatus>(
          `${url}/generate-description-status/?task_id=${encodeURIComponent(data.task_id)}`
        );
        if (generation.current !== request) return;
        if (result.status === 'failed') throw new Error(result.error || 'The description could not be written. Please try again.');
        if (result.status === 'completed') {
          if (!result.description?.trim() || result.description.length > maxLength) {
            throw new Error('The draft was empty or too long. Please try again.');
          }
          setDraft(result.description);
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      if (generation.current === request) throw new Error('Writing took too long. Please try again.');
    } catch (err) {
      if (generation.current === request) setError(getApiErrorMessage(err, err instanceof Error ? err.message : 'Could not write a description. Please try again.'));
    } finally {
      if (generation.current === request) setGenerating(false);
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <FormLabel sx={{ mb: 0 }}>Description</FormLabel>
          <Button size="sm" variant="plain" startDecorator={<Sparkle />} loading={generating}
            disabled={target === 'service' && !serviceName?.trim()} onClick={generate}>
            Write with AI
          </Button>
        </Box>
        <Textarea value={value} onChange={event => onChange(event.target.value)}
          minRows={target === 'business' ? 3 : 2} maxRows={6}
          slotProps={{ textarea: { maxLength } }}
          placeholder={target === 'business' ? 'Tell customers about your business...' : 'Describe this service...'} />
        <Typography level="body-xs" color={value.length > maxLength ? 'danger' : 'neutral'} sx={{ mt: 0.5 }}>
          {value.length}/{maxLength} characters
        </Typography>
      </FormControl>
      {generating && <Typography role="status" level="body-sm">Writing a description…</Typography>}
      {error && <Alert color="danger" role="alert">{error}</Alert>}
      {draft !== null && (
        <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', display: 'grid', gap: 1 }}>
          <Typography level="title-sm">Suggested description</Typography>
          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{draft}</Typography>
          <Typography level="body-xs">Review this draft before using it. It has not been saved or sent to Google.</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="sm" onClick={() => { onChange(draft); setDraft(null); }}>Use description</Button>
            <Button size="sm" variant="plain" color="neutral" onClick={() => setDraft(null)}>Discard</Button>
          </Box>
        </Sheet>
      )}
    </Box>
  );
}
